import { Step } from "../../types/TransactionTemplate.types";
import {
  convertTransactionToSteps,
  buildActionTree,
} from "./transaction.converters";
import { getLogicalBlockFromConnections } from "../../hooks/transactions/useDragPrimitive";
import type { DropTarget } from "../../hooks/transactions/useDragPrimitive";

export function computeMovedStep(
  stepIndex: number,
  step: Step,
  draggedId: string,
  dropTarget: DropTarget,
  originalPositions: Map<string, { x: number; y: number }>,
  originalConnections: Step["connections"],
  selectedTransactionId: number | null,
  _t: (key: string) => string,
): Step | null {
  const primitives = step.primitives.map((p) => {
    const orig = originalPositions.get(p.id);
    return orig ? { ...p, x: orig.x, y: orig.y } : p;
  });
  let connections = originalConnections.map((c) => ({ ...c }));

  const draggedPrim = primitives.find((p) => p.id === draggedId);
  if (!draggedPrim) return null;

  const isComplex =
    draggedPrim.type === "condition" || draggedPrim.type === "loop";

  const draggedBlock = getLogicalBlockFromConnections(
    primitives,
    connections,
    draggedId,
  );

  const incomingToDragged = connections.find((c) => c.to === draggedId);

  let outgoingFromBlock: (typeof connections)[0] | undefined;
  if (isComplex) {
    outgoingFromBlock = connections.find(
      (c) => draggedBlock.has(c.from) && !draggedBlock.has(c.to) && !c.branch,
    );
  } else {
    outgoingFromBlock = connections.find(
      (c) => c.from === draggedId && !c.branch,
    );
  }

  connections = connections.filter((c) => {
    if (c.to === draggedId) return false;
    if (
      outgoingFromBlock &&
      c.from === outgoingFromBlock.from &&
      c.to === outgoingFromBlock.to
    )
      return false;
    return true;
  });

  if (incomingToDragged && outgoingFromBlock) {
    connections.push({
      from: incomingToDragged.from,
      to: outgoingFromBlock.to,
      type: "primitive",
      branch: incomingToDragged.branch,
    });
  }

  let lastInBlock = draggedId;

  const DEFAULT_START_Y = 70;
  const BRANCH_YES_OFFSET = 90;
  const BRANCH_NO_OFFSET = 180;

  let targetY: number;
  if (dropTarget.parentIfId === null) {
    targetY = DEFAULT_START_Y;
  } else {
    const parentPrim = primitives.find((p) => p.id === dropTarget.parentIfId);
    if (!parentPrim) return null;

    if (dropTarget.branch === "yes" || dropTarget.branch === "body") {
      targetY = parentPrim.y + BRANCH_YES_OFFSET;
    } else {
      targetY = parentPrim.y + BRANCH_NO_OFFSET;
    }
  }

  if (draggedPrim) {
    const dy = targetY - draggedPrim.y;
    primitives.forEach((p) => {
      if (draggedBlock.has(p.id)) {
        p.y += dy;
      }
    });
  }

  const targetBranchPrims = primitives
    .filter((p) => !draggedBlock.has(p.id) && Math.abs(p.y - targetY) < 40)
    .sort((a, b) => a.x - b.x);

  let predecessorId: string | null = null;
  let successorId: string | null = null;

  for (const p of targetBranchPrims) {
    if (p.x <= dropTarget.insertAfterX) {
      predecessorId = p.id;
    }
  }
  const predIdx = targetBranchPrims.findIndex((p) => p.id === predecessorId);
  if (predIdx >= 0 && predIdx + 1 < targetBranchPrims.length) {
    successorId = targetBranchPrims[predIdx + 1].id;
  } else if (predecessorId === null && targetBranchPrims.length > 0) {
    successorId = targetBranchPrims[0].id;
  }

  let branchLabel: "yes" | "no" | undefined;
  if (dropTarget.branch === "yes") branchLabel = "yes";
  else if (dropTarget.branch === "no") branchLabel = "no";

  if (predecessorId && successorId) {
    connections = connections.filter(
      (c) => !(c.from === predecessorId && c.to === successorId),
    );
  }

  if (predecessorId) {
    connections.push({
      from: predecessorId,
      to: draggedId,
      type: "primitive",
      branch: undefined,
    });
  } else if (dropTarget.parentIfId) {
    if (successorId) {
      connections = connections.filter(
        (c) =>
          !(
            c.from === dropTarget.parentIfId &&
            c.branch === branchLabel &&
            c.to === successorId
          ),
      );
    }
    connections.push({
      from: dropTarget.parentIfId,
      to: draggedId,
      type: "primitive",
      branch: branchLabel,
    });
  } else {
    if (step.event) {
      if (successorId) {
        connections = connections.filter(
          (c) => !(c.from === step.event!.id && c.to === successorId),
        );
      }
      connections.push({
        from: step.event.id,
        to: draggedId,
        type: "primitive",
      });
    }
  }

  if (successorId) {
    connections.push({
      from: lastInBlock,
      to: successorId,
      type: "primitive",
    });
  }

  const tempStep: Step = { ...step, primitives, connections };
  const actions = buildActionTree(tempStep);

  const recalculated = convertTransactionToSteps({
    id: selectedTransactionId ?? 0,
    actions,
    queues: {},
  });

  const recalcStep = recalculated.steps[0];
  if (!recalcStep) return null;

  return {
    ...step,
    primitives: recalcStep.primitives,
    connections: recalcStep.connections,
    event: recalcStep.event,
  };
}
